<template>
  <div class="changes-container">
    <el-tabs v-model="activeName">
      <el-tab-pane label="Changes" name="default"></el-tab-pane>
      <el-tab-pane label="Advanced" name="json"></el-tab-pane>
    </el-tabs>
    <div v-if="havingChanges" class="changes-wrapper">
      <div v-if="activeName === 'default'">
        <p
          v-for="(change, index) in getChanges()"
          :key="index"
          :style="{
            color: change.color,
          }"
        >
          {{ change.path + " " + change.value }}
        </p>
      </div>
      <div v-else-if="activeName === 'json'" v-html="getChangesAsJson()"></div>
    </div>
    <div
      v-else
      class="changes-wrapper"
      style="place-content: center; place-items: center; display: grid"
    >
      No changes detected
    </div>
  </div>
</template>

<script>
// @ts-nocheck
import * as DeepDiff from "deep-diff";
import * as jsondiffpatch from "jsondiffpatch/dist/jsondiffpatch.umd";
import _ from "lodash";

const CHANGE_COLORS = {
  added: "green",
  removed: "red",
  changed: "orange",
};

/**
 * Builds a human-readable dot/bracket path (e.g. `$.node.quickReplies[1].text`)
 * from deep-diff's path segments, optionally appending an array index.
 */
function buildPath(pathSegments = [], arrayIndex) {
  let path = "$";
  (pathSegments || []).forEach((segment) => {
    path += typeof segment === "number" ? `[${segment}]` : `.${segment}`;
  });
  if (typeof arrayIndex === "number") {
    path += `[${arrayIndex}]`;
  }
  return path;
}

function formatValue(value) {
  if (value === undefined) return "";
  if (value === null) return "null";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/**
 * Describes a single deep-diff edit as { path, value, color }.
 * Handles property add/remove/edit (N/D/E) as well as edits *inside* arrays
 * (kind "A"), so that adding or removing array items such as quick replies,
 * buttons or cards is tracked. human-object-diff silently dropped array
 * insert/remove diffs (a nested-path bug in its array post-processor), which
 * made some content changes appear untracked.
 */
function describeChange(kind, oldValue, newValue, path) {
  switch (kind) {
    case "N":
      return { path, value: `added ${formatValue(newValue)}`, color: CHANGE_COLORS.added };
    case "D":
      return { path, value: `removed ${formatValue(oldValue)}`, color: CHANGE_COLORS.removed };
    case "E":
    default:
      return {
        path,
        value: `changed ${formatValue(oldValue)} to ${formatValue(newValue)}`,
        color: CHANGE_COLORS.changed,
      };
  }
}

export default {
  name: "JsonDiff",
  data: () => ({
    activeName: "default",
  }),
  props: {
    oldValue: {
      type: Object,
      required: true,
    },
    newValue: {
      type: Object,
      required: true,
    },
  },
  methods: {
    getChanges() {
      const differences = DeepDiff.diff(this.oldValue, this.newValue) || [];
      return _.map(differences, (change) => {
        if (change.kind === "A") {
          // Array change: the actual edit is nested in `item`, scoped to `index`.
          const item = change.item || {};
          const path = buildPath(change.path, change.index);
          return describeChange(item.kind, item.lhs, item.rhs, path);
        }
        return describeChange(change.kind, change.lhs, change.rhs, buildPath(change.path));
      });
    },
    getChangesAsJson() {
      jsondiffpatch.formatters.html.hideUnchanged();
      const delta = jsondiffpatch.diff(this.oldValue, this.newValue);
      const changes = jsondiffpatch.formatters.html.format(delta, this.oldValue);
      return changes;
    },
  },
  computed: {
    havingChanges() {
      return !_.isEqual(this.oldValue, this.newValue);
    },
  },
};
</script>

<style scoped lang="scss">
@import "~jsondiffpatch/dist/formatters-styles/html.css";

.changes-container {
  overflow: auto;
  display: grid;
  grid-template-columns: auto;
  background-color: #fff;
  height: fit-content;

  .changes-wrapper {
    margin: 20px;
    padding: 10px;
    border: 2px solid rgba(0, 0, 0, 0.05);
    background: #fdfdfd;
    border-radius: 4px;
    overflow: auto;
  }
}
</style>

<style>
.changes-container .el-tabs__content {
  position: static !important;
}
</style>
